import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Phone, MapPin, AlertCircle, ClipboardCheck, Plus, X, Save } from 'lucide-react';

/**
 * Roof Health Check — recurring maintenance product.
 * Mirrors Access RoofHealthCheckTable. Shows properties whose next health check
 * is due within the configured window, plus a "Log new check" form.
 */
export default function RoofHealthCheckPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [withinDays, setWithinDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [form, setForm] = useState({ overall_state: 'Good', interval_years: 2 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/roof-health-checks/due?within_days=${withinDays}`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, withinDays]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!propertySearch) { setPropertyOptions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/properties?search=${encodeURIComponent(propertySearch)}&limit=10`);
        setPropertyOptions(data.items || []);
      } catch (e) { /* */ }
    }, 250);
    return () => clearTimeout(t);
  }, [api, propertySearch]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) return;
    try {
      await api.post(`/properties/${form.property_id}/health-check`, form);
      setShowForm(false);
      setForm({ overall_state: 'Good', interval_years: 2 });
      setPropertySearch('');
      load();
    } catch (e) { console.error(e); }
  };

  return (
    <div data-testid="roof-health-check-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Roof Health Check</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{count.toLocaleString()}</span> {count === 1 ? 'property' : 'properties'} due in next {withinDays} days</p>
        </div>
        <div className="flex gap-2">
          <select value={withinDays} onChange={e => setWithinDays(Number(e.target.value))} className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
            <option value={90}>Next 90 days</option>
            <option value={180}>Next 6 months</option>
          </select>
          <button data-testid="log-health-check-btn" onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all">
            <Plus size={16} strokeWidth={2.5} /> Log Check
          </button>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : count === 0 ? (
        <div className="bg-white border border-dashed border-[#E2E2DF] rounded-md p-16 text-center">
          <ClipboardCheck size={36} className="text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-slate-500 mb-1">No health checks due in this window</p>
          <p className="text-xs text-slate-400">Click "Log Check" to record an inspection on any property</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-slate-50/80">
              {['Client', 'Property', 'Last Checked', 'Last State', 'Next Due', 'Action'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it._id} className={`border-t border-[#E2E2DF] hover:bg-orange-50/30 cursor-pointer transition-colors group ${it.is_overdue ? 'bg-rose-50/30' : ''}`} onClick={() => navigate(`/properties/${it.property_id}`)}>
                  <td className="px-4 py-3 relative">
                    <div className="absolute inset-y-0 left-0 w-[2px] bg-[#D35400] opacity-0 group-hover:opacity-100 transition-opacity" />
                    <p className="text-sm font-semibold text-slate-800">{it.client_name || 'Unknown'}</p>
                    {it.phone_mobile && <p className="text-xs text-slate-500 font-mono-num flex items-center gap-1 mt-0.5"><Phone size={11} className="text-slate-400" />{it.phone_mobile}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 flex items-center gap-1.5"><MapPin size={11} className="text-slate-400 flex-shrink-0" />{it.address}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 font-mono-num">{it.last_checked || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{it.overall_state || '—'}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold font-mono-num ${it.is_overdue ? 'text-rose-700' : 'text-slate-700'}`}>{it.next_due_date}</span>{it.is_overdue && <span className="ml-1.5 text-[10px] font-semibold text-rose-600 inline-flex items-center gap-1"><AlertCircle size={10} /> Overdue</span>}</td>
                  <td className="px-4 py-3"><span className="text-xs text-[#D35400] font-semibold">Schedule call →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-md max-w-xl w-full max-h-[90vh] overflow-y-auto thin-scroll" onClick={e => e.stopPropagation()}>
            <div className="border-b border-[#E2E2DF] px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-extrabold text-slate-900 font-[Manrope] tracking-tight">New Roof Health Check</h2>
                <p className="text-xs text-slate-500 mt-0.5">Records a brief inspection + sets next-due reminder</p>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100 rounded text-slate-400"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="eyebrow mb-1 block">Property <span className="text-rose-500 normal-case tracking-normal">*</span></label>
                <input data-testid="hc-prop-search" value={propertySearch} onChange={e => { setPropertySearch(e.target.value); setForm(f => ({ ...f, property_id: '' })); }} placeholder="Search address..." className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                {propertyOptions.length > 0 && !form.property_id && (
                  <div className="border border-[#E2E2DF] rounded-md mt-1 max-h-40 overflow-y-auto thin-scroll">
                    {propertyOptions.map(p => (
                      <button key={p._id} type="button" onClick={() => { setForm(f => ({ ...f, property_id: p._id })); setPropertySearch(`${p.property_address_num || ''} ${p.property_address_street || ''}, ${p.property_address_suburb || ''}`.trim()); setPropertyOptions([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 border-b border-[#E2E2DF] last:border-0 transition-colors">
                        {p.property_address_num} {p.property_address_street}, {p.property_address_suburb}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow mb-1 block">Overall state</label>
                  <select value={form.overall_state || ''} onChange={e => setForm(f => ({ ...f, overall_state: e.target.value }))} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    <option>Excellent</option><option>Good</option><option>Fair</option><option>Poor</option><option>Failed</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow mb-1 block">Re-check in (years)</label>
                  <select value={form.interval_years || 2} onChange={e => setForm(f => ({ ...f, interval_years: Number(e.target.value) }))} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'year' : 'years'}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="eyebrow mb-1 block">Issues spotted</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[['moss_lichen', 'Moss / lichen'], ['ridge_capping', 'Ridge capping'], ['fastenings', 'Fastenings'], ['spouting', 'Spouting'], ['flashings', 'Flashings']].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 px-3 py-2 border border-[#E2E2DF] rounded-md text-sm cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={!!form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} className="accent-[#D35400]" /> {l}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="eyebrow mb-1 block">Comments</label>
                <textarea value={form.comments || ''} onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E2DF]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button data-testid="hc-save-btn" type="submit" disabled={!form.property_id} className="flex items-center gap-1.5 px-5 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50 shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all">
                  <Save size={14} strokeWidth={2.5} /> Save Check
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
